<template>
<div>
  <span class="addContactButton">
    <va-button
      @click.prevent="newReference()">
        {{ $t('references.add') }}
    </va-button>
  </span>

  <Breadcrumbs
    :breadcrumbs="[
      { title: this.$t('menu.audiology'), disabled: true },
      { title: this.$t('references.table_header'), disabled: true }
    ]">
    <span slot="breadcrumbAfter" class="d-flex">
        <Searchable
          :querySearch.sync="querySearch"
        />
      <Filters
        :isActive="showExtraFilters"
        :activeFilters="activeFilters"
        @toggle-filters="toggleShowExtraFilters"
      />
      </span>
  </Breadcrumbs>

  <div class="buttonSpace">
    <div v-if="showExtraFilters" class="evollu-form">
        <div class="row">

          <div class="col-12 col-md-4">
            <label class="form-label">{{$t('users.inputs.entity')}}</label>
            <multiselect
              v-model="filters.entities"
              :options="entityOptions"
              track-by="uid"
              :custom-label="formatedEntityLabel"
              label="name"
              select-label=""
              deselect-label="×"
              placeholder="---"
              :searchable="true"
              :allow-empty="true"
              :multiple="true"
              :limit="2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props">
                <div class="multiselect__clear" v-if="filters.entities.length" @mousedown.prevent.stop="filters.entities = []"></div>
              </template>
            </multiselect>
          </div>

          <div class="col-12 col-md-4">
            <label class="form-label">{{ $t('leads.table.status') }}</label>
            <multiselect
              v-model="filters.statuses"
              :options="statuses"
              track-by="id"
              label="name"
              :custom-label="status => $t(status.name)"
              select-label=""
              deselect-label="×"
              placeholder="---"
              :searchable="true"
              :allow-empty="true"
              :multiple="true"
              :limit="3"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props">
                <div class="multiselect__clear" v-if="filters.statuses.length" @mousedown.prevent.stop="filters.statuses = []"></div>
              </template>
            </multiselect>
          </div>

          <div class="col-12 col-md-4">
            <RangeDatePicker v-model="filters.dateRange">
              <div slot="label">
                <label class="form-label" style="margin-top: -3px;">
                  {{ $t('statistics.daterange_start') }}
                  <span class="clickable" :class="{'unselectedDateLabel': filters.dateType != 'created_at', 'selectedDateLabel': filters.dateType == 'created_at'}" @click="filters.dateType = 'created_at'">{{ $t('statistics.daterange_created_at') }}</span>
                  <span class="dateSeparatorLabel">/</span>
                  <span class="clickable" :class="{'unselectedDateLabel': filters.dateType != 'updated_at', 'selectedDateLabel': filters.dateType == 'updated_at'}" @click="filters.dateType = 'updated_at'">{{ $t('statistics.daterange_updated_at') }}</span>
                  {{ $t('statistics.daterange_end') }}:
                </label>
              </div>
            </RangeDatePicker>
          </div>

        </div>
      </div>
    <v-server-table
      :key="tableKey"
      id="referencesTable"
      ref="referencesTable"
      name="referencesTable"
      :columns="columns"
      :options="tableOptions"
      @row-click="onClickRow"
    >
      <span slot="afterFilter" v-if="showResetIcon">
        <i class="fa fa-times reset-filter" @click="resetFilter"></i>
      </span>

      <span slot="name" slot-scope="props">
        <span v-if="props.row.surname">
          {{ props.row.name + ' ' + props.row.surname | capitalize }}
        </span>
        <span v-if="!props.row.surname">
          {{ props.row.name | capitalize }}
        </span>
      </span>
      <span slot="email" slot-scope="props">
        {{ props.row.email ? props.row.email : $t('references.unspecified') }}
      </span>
      <span slot="status" slot-scope="props">
        {{ getStatusByProperty('id', props.row.status) }}
      </span>
      <span slot="serial" slot-scope="props">
        {{ props.row.serial ? props.row.serial : 'N/A' }}
      </span>

      <span slot="updated_at" class="no-break" slot-scope="props">{{ props.row.updated_at | localTime('YYYY-MM-DD HH:mm') }}</span>
      <span slot="info" slot-scope="props">
        <span :title="$t('references.consent.not_verified')" v-if="!props.row.consent && canShowConsent(props.row.entity_id)">
          <i :class="['fa fa-check', {'not-verified': !props.row.consent}]" @click="openConsentModal(props.row)" style="cursor:pointer;"></i>
        </span>
        <span :title="$t('references.table.kiosk')" v-if="props.row.kiosk">
          <i class="fa fa-laptop"></i>
        </span>
        <span :title="$t('references.table.questionnaire')" v-if="props.row.answers">
          <i class="fa fa-question-circle-o"></i>
        </span>
        <span :title="$t('references.table.hearing_test')" v-if="props.row.ht">
          <i class="fa fa-headphones"></i>
        </span>
        <span :title="$t('references.table.marketing')" v-if="props.row.marketing">
          <i class="fa fa-bullhorn"></i>
        </span>
      </span>
    </v-server-table>
  </div>

  <va-modal
    :value="showConsentModal"
    :okToClose="canClose"
    :noOutsideDismiss="true"
    id="consentModal"
    ref="consentModal"
    :cancelText="'modal.cancel' | translate"
    :okText="'references.consent.ok' | translate"
    :okClass="'btn btn-primary btn-micro'"
    cancelClass="btn-cancelar btn btn-primary btn-micro"
    @ok="consentLead"
    :okDisabled="!((!invalidCode && !consentInPaper)|| ((!input1 && !input2 && !input3 && !input4) && consentInPaper))"
    @cancel="cancelConsent">
    <div slot="title">
      {{'references.consent.modal_title' | translate }} {{leadToConsent ? leadToConsent.name : ''}}&nbsp;
      <va-popover
        :message="this.$t('references.consent.print')"
        placement="top">
        <i class="fa fa-print" @click="printConsent"></i>
      </va-popover>
    </div>
    <div>
      <div class="paper">
        <span>{{'references.consent.printed_consent' | translate }}</span>
        <va-checkbox
          :label="'references.consent.lead_signature' | translate"
          :id="'checkbox5'"
          :isCircle="true"
          v-model="consentInPaper"/>
      </div>
      <div v-if="leadToConsent && leadToConsent.email">
      {{'references.consent.didnt_receive_email' | translate }} <b><a href="#" @click.prevent="sendEmailConfirmation"> {{'references.consent.resend_email' | translate }}</a> </b>
      </div>
    </div>
  </va-modal>

  <contact-form
    v-if="showEditModal"
    :entities="entitiesRead"
    :service="component.service"
    :facilitiesWrite="facilitiesWrite"
    :facilitiesRead="facilitiesRead"
    :entitiesWrite="entitiesWrite"
    :editingLead="editingLead"
    @close-contact-modal="closeContactModal"
    @update-table="updateTable">
  </contact-form>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import ContactForm from './ContactForm'
import Multiselect from 'vue-multiselect'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import { TABLE_REFERENCES } from '@/local-storage/LocalStorageKeys'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import IndexTablesMixin from '@/mixins/IndexTablesMixin'
import Searchable from '@/components/common/Searchable'
import Filters from '@/components/common/Filters'

const referencesInfoType = 1000

export default {
  components: {
    Filters,
    Searchable,
    Multiselect,
    ContactForm,
    Breadcrumbs,
    RangeDatePicker
  },
  props: {
    component: {
      type: Object,
      required: true
    }
  },
  mixins: [
    MultiselectLabelFormatter,
    TableQueryMixin,
    ClustersAndEntitiesMixin,
    IndexTablesMixin
  ],
  data () {
    return {
      tableKey: 0,
      querySearch: '',
      customQueries: {},
      localStorageKey: TABLE_REFERENCES,

      leads: [],
      editingLead: null,
      labels: [],
      isloading: false,
      errorMsg: null,
      showEditModal: false,
      show: true,
      showDeleteConfirmation: false,
      deletedReason: '',
      showConsentModal: false,
      canClose: false,
      verification_code: null,
      showExtraFilters: false,
      contactBestTime: [
        {
          i: 0, value: this.$t('references.contactBestTime.select_option')
        },
        {
          i: 1, value: this.$t('references.contactBestTime.early_morning')
        },
        {
          i: 2, value: this.$t('references.contactBestTime.late_morning')
        },
        {
          i: 3, value: this.$t('references.contactBestTime.early_afternoon')
        },
        {
          i: 4, value: this.$t('references.contactBestTime.late_afternoon')
        },
        {
          i: 5, value: this.$t('references.contactBestTime.no_contact')
        }
      ],
      pageCount: 0,
      page: 1,
      queryUrl: `${this.component.service.url}/leads`,
      tableOptions: {
        filterable: ['updated_at', 'name', 'entity', 'appointment_option', 'status'],
        customFilters: ['status', 'entity', 'datestart', 'dateend', 'date'],
        sortable: ['updated_at', 'name', 'email', 'created_at', 'status'],
        headings: {
          name: this.$t('references.table.name'),
          phone: this.$t('references.table.phone'),
          email: this.$t('references.table.email'),
          'entity.name': this.$t('references.table.entity'),
          created_at: this.$t('references.table.created_at'),
          status: this.$t('references.table.status'),
          serial: this.$t('references.table.serial'),
          info: this.$t('references.table.info'),
          updated_at: this.$t('leads.table.updated_at')
        },
        columnsDropdown: true,
        columnsDisplay: {
          name: 'min_mobile',
          phone: 'min_tablet',
          email: 'desktop',
          status: 'min_tablet',
          info: 'desktop',
          updated_at: 'min_mobile'
        },
        columnsClasses: {
          name: 'name',
          phone: 'phone',
          email: 'email',
          entity: 'entity',
          status: 'status',
          info: 'info',
          updated_at: 'updated_at'
        },
        saveState: true,
      },
      input1: '',
      input2: '',
      input3: '',
      input4: '',
      consentInPaper: false,
      leadToConsent: null,
      showResetIcon: false,
      filters: {
        querySearch: null,
        dateType: 'created_at',
        entities: [],
        statuses: [],
        dateRange: {
          start: null,
          end: null
        }
      },
    }
  },
  computed: {
    ...mapGetters({
      statuses: 'leads/statuses',
      clusters: 'clusters'
    }),
    columns: function () {
      return ['name', 'phone', 'email', 'entity.name', 'updated_at', 'status', 'serial', 'info']
    },
    columnsDisplayedByDefault: function () {
      return ['name', 'phone', 'email', 'entity.name', 'updated_at', 'status', 'info']
    },
    audiologyService: function () {
      return this.component.service
    },
    referencesReadPermissions: function () {
      return this.audiologyService.user_permissions.filter(p => p.info_type === referencesInfoType)
    },
    referencesWritePermissions: function () {
      return this.referencesReadPermissions.filter(p => p.access_type >= 2)
    },
    entitiesRead: function () {
      return this.audiologyService.entities.filter(entity => this.referencesReadPermissions.some(p => p.entity_id === null || p.entity_id === entity.id))
    },
    entitiesWrite: function () {
      return this.audiologyService.entities.filter(entity => this.referencesWritePermissions.some(p => p.entity_id === null || p.entity_id === entity.id))
    },
    facilitiesWrite: function () {
      return this.entitiesWrite.map(entity => entity.facilities).flat()
    },
    facilitiesRead: function () {
      return this.entitiesRead.map(entity => entity.facilities).flat()
    },
    activeFilters: function () {
      let nr = this.filters.entities.length + this.filters.statuses.length
      if (this.filters.dateRange !== null && this.filters.dateRange.start !== null && this.filters.dateRange.end !== null) nr += 1
      return nr
    },
    acusticaUser: function () {
      return this.$auth.user().entity.id === 8001 || (this.editingLead && this.editingEntity.id === 8001)
    },
    invalidCode: function () {
      return (!this.validInput1 || !this.validInput2 || !this.validInput3 || !this.validInput4)
    },
    validInput1: function () {
      if (this.input1 === '') return false
      return this.input1.match(/[a-z]/i) === null
    },
    validInput2: function () {
      if (this.input2 === '') return false
      return this.input2.match(/[a-z]/i) === null
    },
    validInput3: function () {
      if (this.input3 === '') return false
      return this.input3.match(/[a-z]/i) === null
    },
    validInput4: function () {
      if (this.input4 === '') return false
      return this.input4.match(/[a-z]/i) === null
    },
    entityOptions: function () {
      return this.createClustersAndEntitiesList(this.clusters, this.entitiesRead)
    }
  },
  methods: {
    showOrHideColumns: function () {
      let state = LocalStorageHelper.get(TABLE_REFERENCES)
      if (state) {
        let displayedColumns = state.userColumnsDisplay || this.columnsDisplayedByDefault
        this.columns
          .filter(column => !displayedColumns.includes(column))
          .forEach(column => this.$refs.referencesTable.toggleColumn(column))
      }
    },
    onClickRow: function ({ row }) {
      this.editingLead = row
      this.showEditModal = true
    },
    getStatusByProperty: function (property, value) {
      let status = this.statuses.find(status => status[property] === value)
      return status ? this.$t(status.name) : null
    },
    closeContactModal: function () {
      this.showEditModal = false
      this.editingLead = null
    },
    toggleShowExtraFilters: function () {
      this.showExtraFilters = !this.showExtraFilters
    },
    hideExtraFilters: function () {
      this.showExtraFilters = false
    },
    updateTable (updatedData) {
      this.closeContactModal()
      let elemIdx = this.$refs.referencesTable.data.findIndex(elem => elem.id === updatedData.data.data.id)
      if (elemIdx !== null && elemIdx !== undefined) {
        this.$refs.referencesTable.data[elemIdx] = updatedData.data.data
      }
    },
    canShowConsent (id) {
      return this.entitiesWrite.some(e => e.id === id)
    },
    newReference () {
      this.showEditModal = true
    },
    printConsent () {
      this.$http({
        url: this.component.service.url + '/' + this.leadToConsent.id + '/consent/print',
        responseType: 'blob',
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'consent.pdf')
        document.body.appendChild(link)
        link.click()
      }).catch((error) => {
        console.log(error)
      })
    },
    cancelConsent () {
      this.consentInPaper = false
      this.input1 = ''
      this.input2 = ''
      this.input3 = ''
      this.input4 = ''
    },
    sendEmailConfirmation () {
      this.isloading = true
      this.$http({
        url: this.component.service.url + '/leads/' + this.leadToConsent.id + '/confirmationemail',
        method: 'POST',
      }).then((response) => {
        this.isloading = false
        this.showToast(this.$t('references.consent.success_email'), this.$store.state.successToastOptions)
      }).catch((error) => {
        this.isloading = false
        let err = Object.keys(error.response.data)[0]
        this.showToast(this.$t('references.consent.' + err), this.$store.state.errorToastOptions)
      })
    },
    consentLead () {
      let verificationCode = this.input1 + this.input2 + this.input3 + this.input4
      if ((!verificationCode || verificationCode === '') && !this.consentInPaper) {
        this.showToast(this.$t('references.consent.code_required'), this.$store.state.errorToastOptions)
        return
      }

      let body = {}
      if (this.consentInPaper) {
        body.in_paper = this.consentInPaper
      }
      if (verificationCode !== '') {
        body.code = verificationCode
      }
      this.$http({
        url: this.component.service.url + '/' + this.leadToConsent.id + '/consent/verify',
        method: 'POST',
        data: body
      }).then((response) => {
        this.showConsentModal = false
        this.input1 = ''
        this.input2 = ''
        this.input3 = ''
        this.input4 = ''
        this.consentInPaper = false
        this.leadToConsent.consent = true
        this.showToast(this.$t('references.consent.success'), this.$store.state.successToastOptions)
      }).catch((error) => {
        let err = Object.keys(error.response.data)[0]
        this.showToast(this.$t('references.consent.' + err), this.$store.state.errorToastOptions)
      })
    },
    openConsentModal (lead) {
      this.leadToConsent = lead
      this.showConsentModal = true
      this.$nextTick(() => {
        this.$refs.consentModal.open()
      })
    },
    loadFiltersLocalStorage: function () {
      const state = LocalStorageHelper.get(TABLE_REFERENCES)
      if (state) {
        Object.values(state.customQueries).some(object => {
          console.log(object)
        })
        if (Object.values(state.customQueries).some(object => object !== null && object !== '')) {
          this.showExtraFilters = true

          if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids) {
            this.filters.entities = this.reloadSelectedClustersAndEntities(this.entityOptions, state.selectedEntityUids)
          }

          if (state.customQueries.status && state.customQueries.status !== '') {
            const selectedStatuses = state.customQueries.status.split(',').map(element => parseInt(element))
            this.filters.statuses = this.statuses.filter(status => selectedStatuses.includes(status.id))
          }

          if (state.customQueries.date !== '') {
            this.filters.dateType = state.customQueries.date
          }

          if (state.customQueries.datestart !== '' && state.customQueries.dateend !== '') {
            this.filters.dateRange = {
              start: state.customQueries.datestart,
              end: state.customQueries.dateend
            }
          }
        }
      }
    },
    writableEntity: function (entityId) {
      for (var i = 0; i < this.entitiesWrite.length; i++) {
        if (this.entitiesWrite[i].id === entityId) {
          return true
        }
      }
      return false
    }
  },
  watch: {
    'filters.statuses': function (statuses) {
      const selectedStatuses = statuses.map(status => status.id).join(',')
      this.emitTableFilter('vue-tables.referencesTable.filter::status', selectedStatuses)
    },
    'filters.entities': function (options) {
      const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
      this.emitTableFilter('vue-tables.referencesTable.filter::entity', Array.from(selectedOptions.ids).join(','))
      this.$nextTick(() => {
        let state = LocalStorageHelper.get(TABLE_REFERENCES)
        state.selectedEntityUids = Array.from(selectedOptions.uids)
        console.log(state)
        LocalStorageHelper.set(TABLE_REFERENCES, state)
      })
    },
    'filters.dateRange': function (dateRange) {
      this.$refs.referencesTable.customQueries.datestart = dateRange ? dateRange.start : null
      this.emitTableFilter('vue-tables.referencesTable.filter::dateend', dateRange ? dateRange.end : null)
    },
    'filters.dateType': function (dateType) {
      this.emitTableFilter('vue-tables.referencesTable.filter::date', dateType)
    },
    'filters.querySearch': function (value) {
      this.$refs.referencesTable.setFilter(value)
      this.$emit('filters-updated', { filters: this.filters, count: this.activeFilterCount })
    },
    querySearch: {
      handler: function (value) {
        this.filters.querySearch = value || ''
      }
    },
    input1: function (value) {
      if (value.length === 0) return
      this.$refs.input2.focus()
    },
    input2: function (value) {
      if (value.length === 0) return
      this.$refs.input3.focus()
    },
    input3: function (value) {
      if (value.length === 0) return
      this.$refs.input4.focus()
    }
  },
  filters: {
    capitalize: function (value) {
      if (!value) return ''
      value = value.toString()
      return value.toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ')
    }
  },
  mounted () {
    this.editingPeriod = this.contactBestTime[0]
    this.$refs.referencesTable.setOrder('updated_at', false)
    this.loadFiltersLocalStorage()
    this.showOrHideColumns()
  }
}
</script>

<style lang="scss">

.no-break {
  white-space: nowrap;
}

.btn.btn-primary.btn-cancelar {
  background-color: #fff;
  color: #29c4a9;
}

.toasted.outline.success {
  color: #29c4a9 !important;
  border-color: #29c4a9 !important;
  box-shadow: 0 4px 9.6px 0.4px #29c4a9 !important;
}

.badge-consent{
  cursor: pointer;
  font-weight: bold;
}

.fa.fa-exclamation-triangle {
  color: red;
}

input.circleInput{
 width: 80px;
 line-height: 80px;
 border-radius: 40px;
 font-size: 40px;
 border-color: #A49D97;
 text-align: center;
 border-style: solid;
 transition: 1s;
}

input:focus.circleInput{
    outline: none;
    border-color: #29c4a9;
}

input[type='number'] {
    -moz-appearance:textfield;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
}

.separator{
  font-size: 50px;
  margin: 5px;
}

.invalid {
  border-color: #e34a4a;
}

.valid{
  border-color: #29c4a9;
}

.validCode{
  border-color: #29c4a9 !important;
}

.invalidInput{
  border-color: red;
}

.verification{
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1em;
  margin-bottom: 2em;
}

ul{
  z-index: 5;
}

[tel]{
  outline: none !important;
}

.fa-print{
  color: #29c4a9;
  font-size: 1.2em;
  cursor: pointer;
}

#referencesTable .verified {
  color: $evollu-dark !important;
}

#referencesTable .not-verified {
  color: $theme-danger !important;
}

#referencesTable .fa-pencil {
  color: $evollu-dark !important;
}

#referencesTable tbody tr:hover .fa-pencil {
  color: white !important;
}

#referencesTable i {
  padding-right: 2px;
  padding-left: 2px;
}

#referencesTable .VueTables__sortable {
  cursor: pointer;
}

#referencesTable th.name {
  min-width: 90px;
}

#referencesTable th.updated_at {
  min-width: 110px;
}

#referencesTable th.info {
  min-width: 85px;
}

#referencesTable th.status {
  min-width: 90px;
}

.unselectedDateLabel {
  font-size: 0.6rem !important;
  color: #ada7a1 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  text-decoration: line-through !important;
}

.dateSeparatorLabel {
  font-size: 0.6rem !important;
  color: #ada7a1 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
}

.selectedDateLabel {
  font-size: 0.6rem !important;
  color: #29c4a9 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
}

.sort-icon-class {
  cursor: pointer;
}

.addContactButton {
  position: fixed;
  right: 20px;
  bottom: 40px;
  z-index: 3;
}

.buttonSpace {
  @media only screen and (max-width: 768px) {
    margin-bottom: 80px !important;
  }
}

</style>
